import { useState, type FormEvent } from 'react'
import { useAuth } from '@core/auth/useAuth'
import { authApi } from '@core/api/auth'
import { ApiError, NetworkError } from '@core/api/client'
import { AVISOS_DE_ENTRADA, avisoDoEndereco } from '@core/auth/avisos'

type Mode = 'password' | 'magic'
type Status = 'idle' | 'sending' | 'sent' | 'error' | 'device-conflict'

const REMEMBER_KEY = 'vn2r.lastLogin'

interface RememberedLogin {
  email: string
  workspace_slug: string
}

function loadRemembered(): RememberedLogin | null {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<RememberedLogin>
    if (
      typeof parsed.email === 'string' &&
      typeof parsed.workspace_slug === 'string'
    ) {
      return { email: parsed.email, workspace_slug: parsed.workspace_slug }
    }
  } catch {
    /* ignore */
  }
  return null
}

function persistRemembered(value: RememberedLogin) {
  try {
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(value))
  } catch {
    /* ignore */
  }
}

function LoginPage() {
  const { refresh, aviso: avisoDaSessao } = useAuth()
  const remembered = loadRemembered()
  // Porque é que a pessoa está aqui: um link que não abriu (vem no
  // endereço, do backend) ou uma sessão que terminou a meio.
  const [aviso] = useState(() => {
    const codigo =
      avisoDoEndereco(window.location.search) ?? avisoDaSessao ?? null
    return codigo ? (AVISOS_DE_ENTRADA[codigo] ?? null) : null
  })
  const [mode, setMode] = useState<Mode>(() =>
    aviso?.pedirNovoLink ? 'magic' : 'password',
  )
  const [email, setEmail] = useState(remembered?.email ?? '')
  const [workspaceSlug, setWorkspaceSlug] = useState(
    remembered?.workspace_slug ?? '',
  )
  const [password, setPassword] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  // Limpa o estado do formulário quando o utilizador troca de modo.
  // Adotado durante o render com guarda de referência: no primeiro
  // render não corre (prevMode === mode), tal como o efeito anterior,
  // que só fazia no-ops na montagem.
  const [prevMode, setPrevMode] = useState(mode)
  if (prevMode !== mode) {
    setPrevMode(mode)
    setStatus('idle')
    setErrorMsg('')
  }

/** Mensagem para um erro que o servidor devolveu.
 *
 *  "Pedido recusado pelo servidor" sozinho não diz nada a ninguém: não
 *  distingue um 500 de um 422, e obriga a ir ao separador de rede do
 *  browser para saber o que se passou. Passa a trazer o que o servidor
 *  explicou, ou, se ele não explicou nada, o código de estado, que é o
 *  mínimo para se poder procurar a causa. */
function mensagemDoServidor(err: ApiError): string {
  const detalhe = (err.body as { detail?: unknown } | null)?.detail
  if (typeof detalhe === 'string' && detalhe.trim()) return detalhe
  // Um 422 do FastAPI traz uma lista de campos em falta ou inválidos.
  if (Array.isArray(detalhe) && detalhe.length > 0) {
    const campos = detalhe
      .map((d) => {
        const loc = (d as { loc?: unknown[] })?.loc
        return Array.isArray(loc) ? String(loc[loc.length - 1]) : null
      })
      .filter(Boolean)
    if (campos.length > 0) {
      return `O servidor recusou os dados enviados (${campos.join(', ')}).`
    }
  }
  return `Pedido recusado pelo servidor (${err.status}).`
}

  const submitPassword = async (force: boolean) => {
    setStatus('sending')
    setErrorMsg('')
    persistRemembered({
      email: email.trim(),
      workspace_slug: workspaceSlug.trim(),
    })
    try {
      await authApi.loginWithPassword(
        email.trim(),
        workspaceSlug.trim(),
        password,
        force,
      )
      // Successful login: server set the session cookie; refresh
      // the auth context so the router promotes the user out of
      // /login.
      await refresh()
    } catch (err) {
      if (err instanceof NetworkError) {
        setStatus('error')
        setErrorMsg('Não foi possível contactar o servidor.')
        return
      }
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setStatus('device-conflict')
          const detail =
            (err.body as { detail?: string } | null)?.detail ??
            'Já existe uma sessão ativa noutro dispositivo.'
          setErrorMsg(detail)
          return
        }
        setStatus('error')
        setErrorMsg(
          err.status === 401
            ? 'Credenciais inválidas.'
            : mensagemDoServidor(err),
        )
        return
      }
      setStatus('error')
      setErrorMsg('Erro inesperado.')
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email || (mode === 'password' && !workspaceSlug)) return
    if (mode === 'password') {
      if (!password) {
        setStatus('error')
        setErrorMsg('Indica a password.')
        return
      }
      await submitPassword(false)
    } else {
      setStatus('sending')
      setErrorMsg('')
      persistRemembered({
        email: email.trim(),
        workspace_slug: workspaceSlug.trim(),
      })
      try {
        await authApi.requestMagicLink(email.trim(), workspaceSlug.trim())
        setStatus('sent')
      } catch (err) {
        setStatus('error')
        if (err instanceof NetworkError) {
          setErrorMsg('Não foi possível contactar o servidor.')
        } else if (err instanceof ApiError) {
          setErrorMsg(mensagemDoServidor(err))
        } else {
          setErrorMsg('Erro inesperado.')
        }
      }
    }
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 bg-[var(--color-bg)] text-[var(--color-text)]">
      <div className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-8 shadow-lg">
        <div className="flex flex-col items-center">
          <img
            src="/branding/vn2r-logo-full-light.svg"
            alt="VN2R, redefining boundaries"
            className="w-64 h-auto"
          />
          <h1 className="mt-5 text-sm font-medium tracking-tight text-[var(--color-text-muted)]">
            <span className="text-[var(--color-accent)]">openBIM</span> Hub
          </h1>
        </div>
        {aviso && (
          <p
            role="status"
            className="mt-4 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-3 py-2 text-xs text-[var(--color-warn-text)]"
          >
            {aviso.texto}
          </p>
        )}
        <p className="mt-4 text-center text-xs text-[var(--color-text-muted)]">
          {mode === 'password'
            ? 'Entrar com email e password'
            : 'Receber link de acesso por email'}
        </p>

        <div className="mt-4 flex gap-1 rounded border border-[var(--color-border)] p-0.5 text-xs">
          {(
            [
              { v: 'password', label: 'Password' },
              { v: 'magic', label: 'Magic link' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMode(opt.v)}
              className={`flex-1 rounded px-2 py-1 ${
                mode === opt.v
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {status === 'sent' && mode === 'magic' ? (
          <div className="mt-6 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] p-3 text-sm text-[var(--color-accent)]">
            Se o email existir e tiver acesso, enviámos um link.
            Verifica a tua caixa de entrada.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                Email
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm text-[var(--color-text)] outline-none focus:border-[var(--color-accent)]"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                Workspace
              </span>
              <input
                type="text"
                required={mode === 'password'}
                autoCapitalize="none"
                autoComplete="off"
                value={workspaceSlug}
                onChange={(e) => setWorkspaceSlug(e.target.value)}
                placeholder="ex.: interno"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm text-[var(--color-text)] outline-none focus:border-[var(--color-accent)]"
              />
              {mode === 'magic' && (
                <span className="text-[11px] text-[var(--color-text-dim)]">
                  Convidados (sem workspace): deixem em branco.
                </span>
              )}
            </label>
            {mode === 'password' && (
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                  Password
                </span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm text-[var(--color-text)] outline-none focus:border-[var(--color-accent)]"
                />
              </label>
            )}
            {status === 'device-conflict' && errorMsg && (
              <div className="flex flex-col gap-2 rounded border border-[var(--color-warn-border)] bg-[var(--color-accent-bg)] p-3 text-xs text-[var(--color-warn-text)]">
                <span>{errorMsg}</span>
                <button
                  type="button"
                  onClick={() => void submitPassword(true)}
                  className="self-start rounded border border-[var(--color-accent-border)] px-2 py-1 text-xs font-medium text-[var(--color-warn-text)] hover:bg-[var(--color-accent-bg)]"
                >
                  Forçar entrada (terminar a outra sessão)
                </button>
              </div>
            )}
            {status === 'error' && errorMsg && (
              <div className="text-xs text-red-400">{errorMsg}</div>
            )}
            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-2 rounded bg-[var(--color-accent)] px-3 py-2 text-sm font-medium text-[var(--color-bg)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status === 'sending'
                ? 'A enviar…'
                : mode === 'password'
                  ? 'Entrar'
                  : aviso?.pedirNovoLink
                    ? 'Enviar novo link'
                    : 'Receber link'}
            </button>
            {mode === 'password' && (
              <p className="text-[11px] text-[var(--color-text-dim)]">
                Sem password definida ainda? Usa o magic link para
                entrar e depois cria uma password no menu da topbar.
              </p>
            )}
          </form>
        )}
      </div>
      <a
        href="https://www.vn2r.pt"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]"
      >
        www.vn2r.pt
      </a>
    </div>
  )
}

export default LoginPage
